import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

/**
 * Backstop poll interval.
 *
 * Reservations are hour-scale, but they are CREATED at unpredictable moments and from outside
 * this browser entirely — the OCS portal books one with no page open anywhere. Polling is
 * therefore the only signal that covers every source, and the triggers below exist to stop a
 * user staring at a stale page while they wait for it.
 */
const POLL_MS = 30000;

/**
 * Refresh promptly when something has plausibly changed, instead of only on the next poll.
 *
 * Three signals, covering the three ways a reservation appears while a page is already open:
 *
 *   `reservationsChanged`  ObservatoryReservationsPage already broadcasts this after an admin
 *                          creates, edits or cancels a block. TimelineView and
 *                          ScheduleAnalyticsPane listen for it; so should we, or the admin who
 *                          just made a reservation sees every other view update but not this one.
 *   focus / visibility     covers a reservation made anywhere else — another tab, the portal, a
 *                          colleague — because coming back to the tab is exactly when a stale
 *                          answer is most likely and most visible.
 *
 * Returns a detach function; callers must invoke it on cleanup or every remount leaves listeners
 * behind, and a component that mounts on every navigation would accumulate them.
 *
 * Exported because the timeline views own their reservation fetches rather than using the hooks
 * here, and were stale for the same reason: they load once and rely on a message that, in
 * practice, almost never arrives.
 */
export function attachRefreshTriggers(load, observatoryId) {
  const onMessage = (event) => {
    if (event?.data?.type !== 'reservationsChanged') return;
    // A broadcast naming a different observatory is not ours to act on, but one with no id at
    // all is treated as "something changed" rather than ignored.
    const changed = event.data.observatory_id;
    if (observatoryId && changed && String(changed) !== String(observatoryId)) return;
    load();
  };
  const onFocus = () => load();
  const onVisibility = () => {
    if (document.visibilityState === 'visible') load();
  };

  window.addEventListener('message', onMessage);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/**
 * The reservation covering *now* for an observatory, or null.
 *
 * A reserved observatory has been deliberately taken out of the dispatch pool —
 * `target_request` refuses work while a reservation covers the current instant, and
 * fails closed if the check itself errors. Screens that show availability therefore
 * have to account for it, or they report the opposite of the truth: an idle pane
 * saying "waiting for next target assignment", or a green "Dispatching" chip, while
 * the scheduler is declining every poll.
 *
 * Shared rather than duplicated per screen so two views cannot disagree about
 * whether the telescope is currently held.
 *
 * Asks the server for the window [now, now]. The list endpoint filters by overlap
 * (`start_time < to AND end_time > from`), so a zero-width window returns exactly
 * the reservations spanning this instant, and `effective_status` is derived by the
 * same helper the dispatch guard uses — the UI and the scheduler cannot drift apart
 * on the definition.
 *
 * Fails soft. An error yields null, which renders as "not reserved" — the
 * pre-existing behaviour of every caller, rather than a broken screen.
 *
 * @param {string} observatoryId
 * @param {number} pollMs polling interval; reservations are hour-scale, so the
 *   default trades a minute of staleness at each boundary for a near-idle request rate.
 */
export default function useActiveReservation(observatoryId, pollMs = POLL_MS) {
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    if (!observatoryId) {
      setReservation(null);
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const now = new Date().toISOString();
        const resp = await apiService.get(
          `/v1/observatories/${observatoryId}/reservations`,
          { params: { from: now, to: now } },
        );
        const rows = resp?.data?.data || [];
        // The endpoint already excludes cancelled rows unless asked for them; the
        // status check additionally drops anything whose window has moved on
        // between the query and the response.
        const current = rows.find((r) => r.effective_status === 'active') || null;
        if (!cancelled) setReservation(current);
      } catch (e) {
        if (!cancelled) setReservation(null);
      }
    };

    load();
    const timer = setInterval(load, pollMs);
    const detach = attachRefreshTriggers(load, observatoryId);
    return () => { cancelled = true; clearInterval(timer); detach(); };
  }, [observatoryId, pollMs]);

  return reservation;
}

/**
 * When the block lifts, as a display string. Honours an early cancellation.
 *
 * UTC with an explicit suffix, matching every other time in the operator views
 * (ObservatorySchedulePulse, PastObservationsCard, the plugin log). Observatory
 * scheduling is reasoned about in UTC, and the browser's local zone is not
 * necessarily the telescope's -- a local-time string with no zone label is worse
 * than useless here, because it looks correct.
 */
export function reservationHeldUntil(reservation) {
  if (!reservation) return null;
  const until = new Date(reservation.effective_blocking_end || reservation.end_time);
  if (Number.isNaN(until.getTime())) return null;
  return `${until.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/**
 * Every observatory currently reserved, as a map of observatoryId -> reservation.
 *
 * For list views. Asking per row would be one request per observatory on every render,
 * which is why the server exposes `/v1/reservations/active` as a single call.
 *
 * Same failure posture as the singular hook: an error yields an empty map, which renders
 * as "nothing is reserved" rather than breaking the list.
 */
export function useActiveReservations(pollMs = POLL_MS) {
  const [byObservatory, setByObservatory] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await apiService.get('/v1/reservations/active');
        if (!cancelled) setByObservatory(resp?.data?.data || {});
      } catch (e) {
        if (!cancelled) setByObservatory({});
      }
    };
    load();
    const timer = setInterval(load, pollMs);
    // No observatory filter: a list view cares about a change to any row.
    const detach = attachRefreshTriggers(load, null);
    return () => { cancelled = true; clearInterval(timer); detach(); };
  }, [pollMs]);

  return byObservatory;
}
